import React from 'react'
import ConstraintPin from '../detail/timeline/ConstraintPin'
import type { PrintLabels } from './print-labels'

export interface PrintFooterProps {
  x: number
  y: number
  width: number
  showLegend: boolean
  labels: PrintLabels
  pageText: string
}

const SWATCH_W = 22
const SWATCH_H = 10
const LABEL_GAP = 6
const ITEM_GAP = 16
/** Average glyph width at `text-xs`; only used to lay the legend items out left to right. */
const CHAR_W = 6.5
const SWATCH_TOP = 8
/**
 * The legend may only use this much of the body width. The page counter is right-aligned at the
 * body's right edge, so a legend laid out to the full width printed its last labels straight
 * through it — worst on A4 portrait with the longer Czech words.
 */
const LEGEND_MAX_FRACTION = 0.8
/** Same bracket geometry as `SummaryBar`, scaled down to the swatch height. */
const CAP_HALF_W = 4
const CAP_H = 5
const SUMMARY_H = 5
const DIAMOND_HALF = 5
const BASELINE_H = 4

/**
 * Swatches use the same classes as the real components (`fill-primary`, `fill-destructive`,
 * `fill-foreground`, `fill-muted-foreground/60`) so the legend cannot drift away from what the
 * timeline actually draws; the constraint swatch renders `ConstraintPin` itself.
 */
const swatch = (key: string, x: number, y: number): React.ReactNode => {
  const cx = x + SWATCH_W / 2
  const cy = y + SWATCH_H / 2
  switch (key) {
    case 'task':
      return <rect x={x} y={y} width={SWATCH_W} height={SWATCH_H} rx={2} className="fill-primary" />
    case 'critical':
      return <rect x={x} y={y} width={SWATCH_W} height={SWATCH_H} rx={2} className="fill-destructive" />
    case 'milestone':
      return <polygon points={`${cx},${cy - DIAMOND_HALF} ${cx + DIAMOND_HALF},${cy} ${cx},${cy + DIAMOND_HALF} ${cx - DIAMOND_HALF},${cy}`} className="fill-primary" />
    case 'summary': {
      const top = y + 1
      const capY = top + SUMMARY_H
      return (
        <g>
          <rect x={x} y={top} width={SWATCH_W} height={SUMMARY_H} className="fill-foreground" />
          <polygon points={`${x - CAP_HALF_W},${capY} ${x + CAP_HALF_W},${capY} ${x},${capY + CAP_H}`} className="fill-foreground" />
          <polygon points={`${x + SWATCH_W - CAP_HALF_W},${capY} ${x + SWATCH_W + CAP_HALF_W},${capY} ${x + SWATCH_W},${capY + CAP_H}`} className="fill-foreground" />
        </g>
      )
    }
    case 'baseline':
      return <rect x={x} y={y + (SWATCH_H - BASELINE_H) / 2} width={SWATCH_W} height={BASELINE_H} rx={2} className="fill-muted-foreground/60" />
    default:
      return <ConstraintPin x={cx} rowTop={y - 4} />
  }
}

/** Legend (optional) plus the always-present page counter, right-aligned at the body's right edge (spec §4). */
const PrintFooter: React.FC<PrintFooterProps> = ({ x, y, width, showLegend, labels, pageText }) => {
  const swatchY = y + SWATCH_TOP
  const items = [
    { key: 'task', label: labels.legend.task },
    { key: 'critical', label: labels.legend.critical },
    { key: 'milestone', label: labels.legend.milestone },
    { key: 'summary', label: labels.legend.summary },
    { key: 'baseline', label: labels.legend.baseline },
    { key: 'constraint', label: labels.legend.constraint },
  ]
  // Items are laid out until the next one would cross the page counter; the rest are dropped
  // rather than overprinted (the legend is a reminder, the counter is navigation).
  const limit = x + width * LEGEND_MAX_FRACTION
  let cursor = x
  const placed: { key: string; label: string; x: number }[] = []
  for (const it of items) {
    const itemW = SWATCH_W + LABEL_GAP + it.label.length * CHAR_W
    if (cursor + itemW > limit) break
    placed.push({ key: it.key, label: it.label, x: cursor })
    cursor += itemW + ITEM_GAP
  }

  return (
    <g data-print-footer>
      {showLegend && placed.map((it) => (
        <g key={it.key}>
          {swatch(it.key, it.x, swatchY)}
          <text x={it.x + SWATCH_W + LABEL_GAP} y={swatchY + SWATCH_H / 2} dominantBaseline="middle" className="text-xs fill-muted-foreground">{it.label}</text>
        </g>
      ))}
      <text x={x + width} y={y + 18} textAnchor="end" className="text-xs fill-muted-foreground">{pageText}</text>
    </g>
  )
}

export default PrintFooter
